import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { LuArrowDownLeft } from 'react-icons/lu';
import { GoArrowUpRight } from 'react-icons/go';
import { MdOutlineNotifications } from 'react-icons/md';
import Select, { Option } from 'components/Common/Select';
import { titleCase } from 'utils/helperFunctions';
import { getEndReasonBadgeColor } from 'utils/endReasonBadgeColor';
import { formatDisconnectionReason } from 'utils/disconnectionReason';
import type { Control, UseFormSetValue } from 'react-hook-form';
import type { InteractionNotification } from '@/apis/business/agent/callLogs/types';
import { useGetInteractionsLogs } from 'apis/business/agent/callLogs/hook';
import { useGetAgencyCallLogs } from 'apis/agency/callLogs/hook';
import NotificationWithBadgeIcon from '@/assets/icons/NotificationWithBadgeIcon';
import { useLocation } from 'react-router-dom';
import { useGetBusinessInteractionsLogs } from '@/apis/business/hook';

const PAGE_SIZE = 7;

type InteractionType = 'call' | 'chat';

type CallLogItem = {
  id?: number;
  channel?: string;
  start_at?: string;
  interaction_type?: InteractionType;
  direction?: 'inbound' | 'outbound';
};

interface FormValues {
  contact: { call: number | string; [key: string]: unknown };
  note: { description: string };
}

function toOption(item: CallLogItem, formatLogDate: (date: string) => string): Option {
  return {
    label: `${titleCase(item.channel ?? '')} - ${formatLogDate(item.start_at ?? '')}`,
    value: item.id ?? 0
  };
}

export default function InteractionSelector({
  callLog,
  control,
  contactId,
  isAgencyView = false,
  chatsCount,
  callsCount,
  formatLogDate,
  endReason,
  endReasonLabel,
  notifications = [],
  onOpenNotifications,
  onSelect,
  setValue,
  showInteractionTypeFilter = false
}: {
  callLog?: CallLogItem;
  control: Control<FormValues>;
  contactId?: number;
  isAgencyView?: boolean;
  chatsCount?: number;
  callsCount?: number;
  formatLogDate: (date: string) => string;
  endReason?: string;
  endReasonLabel?: string;
  notifications?: InteractionNotification[];
  onOpenNotifications: () => void;
  onSelect: (id: number | undefined) => void;
  setValue: UseFormSetValue<FormValues>;
  showInteractionTypeFilter?: boolean;
}) {
  const theme = useTheme();
  const location = useLocation();
  const isContactPageView = location?.pathname === '/setting/contacts';
  const [selectedType, setSelectedType] = useState<InteractionType>(callLog?.interaction_type ?? 'call');
  const [currentPage, setCurrentPage] = useState(1);
  const [interactionOptions, setInteractionOptions] = useState<Option[]>([]);

  useEffect(() => {
    if (!callLog?.interaction_type) return;
    setSelectedType((prev) => {
      if (prev === callLog.interaction_type) return prev;
      setCurrentPage(1);
      setInteractionOptions([]);
      return callLog.interaction_type!;
    });
  }, [callLog?.id, callLog?.interaction_type]);

  useEffect(() => {
    setCurrentPage(1);
    setInteractionOptions([]);
  }, [contactId, showInteractionTypeFilter]);

  const listParams = useMemo(() => {
    const params: Record<string, unknown> = {
      include: 'contact,agent,business,intent_signal',
      'filter[contact_id_eq]': contactId,
      page: currentPage,
      per_page: PAGE_SIZE
    };

    if (showInteractionTypeFilter) {
      params['filter[interaction_type_eq]'] = selectedType === 'call' ? 0 : 1;
    }

    return params;
  }, [contactId, currentPage, selectedType, showInteractionTypeFilter]);

  const locationQuery = useGetInteractionsLogs(listParams, {
    enabled: Boolean(contactId) && !isAgencyView && !isContactPageView
  } as any);
  const agencyQuery = useGetAgencyCallLogs(listParams, {
    enabled: Boolean(contactId) && isAgencyView
  });
  const businessQuery = useGetBusinessInteractionsLogs(listParams, {
    enabled: Boolean(contactId) && isContactPageView
  } as any);

  const activeQuery = isAgencyView ? agencyQuery : isContactPageView ? businessQuery : locationQuery;
  const pageData = activeQuery.data?.data;
  const isLoadingOptions = activeQuery.isLoading;
  const totalPages = activeQuery.data?.meta?.total_pages ?? 1;
  const isInitialLoading = activeQuery.isLoading && currentPage === 1 && interactionOptions.length === 0;
  const isFetchingPage = activeQuery.isFetching;

  useEffect(() => {
    if (!pageData) return;

    setInteractionOptions((prevOptions) => {
      const existingIds = new Set(prevOptions.map((option) => option.value));
      const newOptions = pageData
        .filter((item) => item.id != null && !existingIds.has(item.id))
        .map((item) => toOption(item as CallLogItem, formatLogDate));
      return [...prevOptions, ...newOptions];
    });
  }, [pageData, formatLogDate]);

  const options = useMemo(() => {
    const currentId = callLog?.id;
    if (currentId == null) return interactionOptions;

    const currentInOptions = interactionOptions.some((option) => String(option.value) === String(currentId));
    if (currentInOptions) return interactionOptions;

    const matchesSelectedType = !showInteractionTypeFilter || callLog?.interaction_type === selectedType;
    if (!matchesSelectedType) return interactionOptions;

    // Selected log not in loaded pages yet — keep API order and append at bottom only
    return [...interactionOptions, toOption(callLog as any, formatLogDate)];
  }, [interactionOptions, callLog, formatLogDate, showInteractionTypeFilter, selectedType]);

  useEffect(() => {
    if (!showInteractionTypeFilter || isFetchingPage) return;

    const currentMatchesType = callLog?.interaction_type === selectedType;
    if (currentMatchesType) return;

    const nextId = pageData?.[0]?.id ?? (interactionOptions[0]?.value as number | undefined);
    if (nextId != null) {
      onSelect(Number(nextId));
      setValue('contact.call', Number(nextId));
    } else if (currentPage === 1 && !isFetchingPage) {
      onSelect(undefined);
      setValue('contact.call', '');
    }
  }, [
    showInteractionTypeFilter,
    selectedType,
    pageData,
    interactionOptions,
    callLog?.interaction_type,
    isFetchingPage,
    currentPage,
    onSelect,
    setValue
  ]);

  const isChatDisabled = showInteractionTypeFilter && (chatsCount ?? 0) === 0;
  const isCallDisabled = showInteractionTypeFilter && (callsCount ?? 0) === 0;

  const activeType = showInteractionTypeFilter ? selectedType : callLog?.interaction_type;
  const notificationCount = notifications.length;
  const notificationTooltip =
    notificationCount === 1 ? '1 Notification Sent' : `${notificationCount} Notifications Sent`;

  const handleTypeChange = (type: InteractionType) => {
    if (type === selectedType) return;
    if (type === 'chat' && isChatDisabled) return;
    if (type === 'call' && isCallDisabled) return;
    setSelectedType(type);
    setCurrentPage(1);
    setInteractionOptions([]);
  };

  const handleScrollEnd = () => {
    if (isFetchingPage) return;
    if (currentPage >= totalPages) return;
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const notificationBell = (
    <Tooltip title={notificationCount > 0 ? notificationTooltip : ''} arrow placement='top'>
      <IconButton
        onClick={onOpenNotifications}
        aria-label='View notifications'
        sx={{
          bgcolor: '#FFF7E6',
          borderRadius: 1.5,
          width: 36,
          height: 36,
          p: 0,
          '&:hover': {
            bgcolor: '#FFEFCC'
          }
        }}
      >
        {notificationCount > 0 ? <NotificationWithBadgeIcon /> : <MdOutlineNotifications size={22} color='#D97706' />}
      </IconButton>
    </Tooltip>
  );

  const loadingOptions = isInitialLoading || isLoadingOptions || (isFetchingPage && currentPage > 1);

  return (
    <Box>
      {showInteractionTypeFilter ? (
        <RadioGroup
          row
          value={selectedType}
          onChange={(_, value) => handleTypeChange(value as InteractionType)}
          sx={{ mb: 1, gap: 1 }}
        >
          <FormControlLabel
            value='call'
            disabled={isCallDisabled}
            control={<Radio size='small' />}
            label='Call'
            sx={{ mr: 2, ml: 0 }}
          />
          <FormControlLabel
            value='chat'
            control={<Radio size='small' />}
            label='Chat'
            disabled={isChatDisabled}
            sx={{ mr: 0, ml: 0 }}
          />
        </RadioGroup>
      ) : null}

      <Box display='flex' alignItems='center' gap={1}>
        <Box flex={1}>
          <Select
            name='contact.call'
            label={activeType === 'call' ? 'Calls' : 'Chats'}
            control={control}
            fullWidth
            sortOrder='none'
            disabled={loadingOptions}
            isLoading={loadingOptions}
            options={options}
            onScrollEnd={handleScrollEnd}
            onChange={(e) => {
              onSelect(Number(e.target.value));
              setValue('contact.call', Number(e.target.value));
            }}
            sx={{ background: '#fff' }}
          />
        </Box>
        {activeType === 'call' ? (
          <Stack direction='row' alignItems='center' spacing={1}>
            {callLog?.direction ? (
              <Tooltip title={callLog.direction === 'inbound' ? 'Inbound' : 'Outbound'} arrow placement='top'>
                <Box
                  sx={{
                    bgcolor: callLog?.direction === 'inbound' ? theme.palette.success.light : theme.palette.blue.light,
                    borderRadius: 1.5,
                    px: 0.9,
                    pt: 0.7,
                    pb: 0.3
                  }}
                >
                  {callLog.direction === 'inbound' ? (
                    <LuArrowDownLeft size={20} color={theme.palette.success.main} />
                  ) : (
                    <GoArrowUpRight size={20} color={theme.palette.blue.main} />
                  )}
                </Box>
              </Tooltip>
            ) : null}
            {notificationBell}
            {endReason ? (
              <Typography
                sx={{
                  display: 'inline-block',
                  fontSize: 12,
                  borderRadius: 6,
                  px: 1.5,
                  py: 0.5,
                  fontWeight: 500,
                  color: getEndReasonBadgeColor(endReason, theme)?.main,
                  bgcolor: getEndReasonBadgeColor(endReason, theme)?.bg,
                  whiteSpace: 'nowrap'
                }}
              >
                {formatDisconnectionReason(endReason, endReasonLabel)}
              </Typography>
            ) : null}
          </Stack>
        ) : (
          notificationBell
        )}
      </Box>
    </Box>
  );
}
